import React, { useState, useEffect } from 'react';
import {
  Layout,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Shield,
  Layers,
  Eye,
  Info
} from 'lucide-react';
import {
  WIDGET_CATALOG,
  ConfiguredWidgetItem,
  WidgetSize,
  getAllowedWidgetsForRole,
  getRoleDefaultLayout,
  saveRoleDefaultLayout,
  resetRoleToFactoryDefault
} from '../dashboard/dashboardLayoutConfig';
import { ROLE_MODULE_PERMISSIONS } from '../../shared/rolePermissions';

export const RoleDashboardLayoutWorkspace: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<string>('SAFETY_MANAGER');
  const [layout, setLayout] = useState<ConfiguredWidgetItem[]>([]);
  const [selectedWidgetToAdd, setSelectedWidgetToAdd] = useState<string>('');
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // Load layout whenever selected role changes
  useEffect(() => {
    const current = getRoleDefaultLayout(selectedRole);
    setLayout(current);
    setSelectedWidgetToAdd('');
    setSaveSuccess(null);
  }, [selectedRole]);

  const allowedCatalog = getAllowedWidgetsForRole(selectedRole);
  const activeWidgetIds = layout.map((item) => item.widgetId);
  const availableToAdd = allowedCatalog.filter((item) => !activeWidgetIds.includes(item.id));

  // Role permissions display
  const rolePermissions = ROLE_MODULE_PERMISSIONS[selectedRole] || [];

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...layout];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    // re-index order
    updated.forEach((item, idx) => {
      item.order = idx + 1;
    });
    setLayout(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === layout.length - 1) return;
    const updated = [...layout];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    // re-index order
    updated.forEach((item, idx) => {
      item.order = idx + 1;
    });
    setLayout(updated);
  };

  const handleSizeChange = (id: string, newSize: WidgetSize) => {
    setLayout((prev) =>
      prev.map((item) => (item.id === id ? { ...item, size: newSize } : item))
    );
  };

  const handleRemove = (id: string) => {
    setLayout((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      filtered.forEach((item, idx) => {
        item.order = idx + 1;
      });
      return filtered;
    });
  };

  const handleAddWidget = () => {
    if (!selectedWidgetToAdd) return;
    const catalogItem = WIDGET_CATALOG.find((w) => w.id === selectedWidgetToAdd);
    if (!catalogItem) return;

    const newItem: ConfiguredWidgetItem = {
      id: `cfg-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      widgetId: catalogItem.id,
      size: catalogItem.defaultSize,
      order: layout.length + 1
    };

    setLayout([...layout, newItem]);
    setSelectedWidgetToAdd('');
  };

  const handleSave = () => {
    saveRoleDefaultLayout(selectedRole, layout);
    setSaveSuccess(`Default layout for role "${selectedRole}" saved successfully.`);
    setTimeout(() => setSaveSuccess(null), 4000);
  };

  const handleResetToFactory = () => {
    if (window.confirm(`Reset "${selectedRole}" dashboard layout to factory default?`)) {
      const reset = resetRoleToFactoryDefault(selectedRole);
      setLayout(reset);
      setSaveSuccess(`Reset "${selectedRole}" to platform factory default layout.`);
      setTimeout(() => setSaveSuccess(null), 4000);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: '#ffffff',
          borderRadius: '16px',
          padding: '24px 28px',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <Layout size={22} color="#60a5fa" />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
            Role-Based Dashboard Layout Configuration
          </h2>
        </div>
        <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.5, maxWidth: '820px' }}>
          Configure the default widgets, display sequence, and card sizes for each organizational role.
          Users belonging to the selected role will see this layout as their default dashboard cockpit.
          Roles can only access widgets corresponding to their authorized security modules.
        </p>
      </div>

      {/* Role Selection & Permissions Header */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
              Target Role Profile:
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              style={{
                padding: '9px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: 600,
                color: '#0f172a',
                backgroundColor: '#f8fafc',
                cursor: 'pointer'
              }}
            >
              <optgroup label="Safety & Risk Roles">
                <option value="SAFETY_MANAGER">Safety Manager (SAFETY_MANAGER)</option>
                <option value="INVESTIGATION_MANAGER">Investigation Manager (INVESTIGATION_MANAGER)</option>
                <option value="RISK_MANAGER">Risk Manager (RISK_MANAGER)</option>
              </optgroup>
              <optgroup label="Quality & Compliance Roles">
                <option value="QUALITY_MANAGER">Quality Manager (QUALITY_MANAGER)</option>
                <option value="AUDIT_MANAGER">Audit Manager (AUDIT_MANAGER)</option>
                <option value="AUDITOR">Auditor (AUDITOR)</option>
                <option value="COMPLIANCE_MANAGER">Compliance Manager (COMPLIANCE_MANAGER)</option>
              </optgroup>
              <optgroup label="Operations & Specialized Roles">
                <option value="MOC_MANAGER">MOC Manager (MOC_MANAGER)</option>
                <option value="LINE_PILOT">Line Captain Pilot (LINE_PILOT)</option>
                <option value="MAINTENANCE_TECH">Maintenance Technician (MAINTENANCE_TECH)</option>
              </optgroup>
              <optgroup label="System Governance">
                <option value="SYSTEM_ADMIN">System Administrator (SYSTEM_ADMIN)</option>
                <option value="ORGANIZATION_ADMIN">Organization Administrator (ORGANIZATION_ADMIN)</option>
              </optgroup>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={handleResetToFactory}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              title="Reset role default to factory layout"
            >
              <RotateCcw size={14} />
              <span>Reset to Factory Default</span>
            </button>

            <button
              type="button"
              onClick={handleSave}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#0f172a',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <Save size={15} />
              <span>Save Role Default</span>
            </button>
          </div>
        </div>

        {/* Security Modules Allowed Notice */}
        <div
          style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '10px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}
        >
          <Shield size={16} color="#1d4ed8" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div style={{ fontSize: '12px', color: '#1e40af', lineHeight: 1.5 }}>
            <b>Security Boundary for {selectedRole}:</b> This role has access to{' '}
            <b>{rolePermissions.includes('*') ? 'ALL modules' : `${rolePermissions.length} modules`}</b>.{' '}
            Only widgets belonging to these domain modules can be selected:
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
              {allowedCatalog.map((item) => (
                <span
                  key={item.id}
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #93c5fd',
                    color: '#1e40af',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: '6px'
                  }}
                >
                  {item.title} ({item.requiredModule})
                </span>
              ))}
            </div>
          </div>
        </div>

        {saveSuccess && (
          <div
            style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#166534',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <CheckCircle2 size={16} />
            <span>{saveSuccess}</span>
          </div>
        )}
      </div>

      {/* Widget Layout Sequence & Sizing Table */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
              Configured Widgets ({layout.length})
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
              Arrange the display order and column widths for {selectedRole} users.
            </p>
          </div>

          {/* Add Widget for this role */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={selectedWidgetToAdd}
              onChange={(e) => setSelectedWidgetToAdd(e.target.value)}
              disabled={availableToAdd.length === 0}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '12.5px',
                color: '#0f172a',
                backgroundColor: availableToAdd.length === 0 ? '#f1f5f9' : '#ffffff',
                cursor: availableToAdd.length === 0 ? 'not-allowed' : 'pointer',
                minWidth: '240px'
              }}
            >
              <option value="">
                {availableToAdd.length === 0
                  ? '-- All authorized widgets already added --'
                  : '-- Select Authorized Widget to Add --'}
              </option>
              {availableToAdd.map((w) => (
                <option key={w.id} value={w.id}>
                  [{w.category}] {w.title} ({w.requiredModule})
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={handleAddWidget}
              disabled={!selectedWidgetToAdd}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: selectedWidgetToAdd ? '#2563eb' : '#94a3b8',
                color: '#ffffff',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: selectedWidgetToAdd ? 'pointer' : 'not-allowed'
              }}
            >
              <Plus size={14} />
              <span>Add Widget</span>
            </button>
          </div>
        </div>

        {/* Widgets Ordered List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {layout.map((item, index) => {
            const catalogItem = WIDGET_CATALOG.find((w) => w.id === item.widgetId);
            if (!catalogItem) return null;

            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  gap: '14px',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
                }}
              >
                {/* Order Index & Title */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      backgroundColor: '#f1f5f9',
                      color: '#0f172a',
                      fontSize: '12px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {item.order}
                  </span>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                        {catalogItem.title}
                      </span>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: '4px',
                          backgroundColor:
                            catalogItem.category === 'METRIC'
                              ? '#dbeafe'
                              : catalogItem.category === 'TABLE'
                              ? '#fef3c7'
                              : '#ede9fe',
                          color:
                            catalogItem.category === 'METRIC'
                              ? '#1e40af'
                              : catalogItem.category === 'TABLE'
                              ? '#b45309'
                              : '#6d28d9'
                        }}
                      >
                        {catalogItem.category}
                      </span>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: '#64748b',
                          fontFamily: 'monospace'
                        }}
                      >
                        {catalogItem.code}
                      </span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                      {catalogItem.description} • Required Module: <b>{catalogItem.requiredModule}</b>
                    </div>
                  </div>
                </div>

                {/* Size Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                    Size:
                  </label>
                  <select
                    value={item.size}
                    onChange={(e) => handleSizeChange(item.id, e.target.value as WidgetSize)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      color: '#0f172a',
                      backgroundColor: '#f8fafc',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="1-col">1 Column (1/4 Width)</option>
                    <option value="2-col">2 Columns (Half Width)</option>
                    <option value="full">Full Width (1/1 Width)</option>
                  </select>
                </div>

                {/* Move Up / Down Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    style={{
                      background: 'none',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      padding: '5px',
                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                      color: index === 0 ? '#cbd5e1' : '#475569'
                    }}
                    title="Move Up"
                  >
                    <ArrowUp size={14} />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === layout.length - 1}
                    style={{
                      background: 'none',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      padding: '5px',
                      cursor: index === layout.length - 1 ? 'not-allowed' : 'pointer',
                      color: index === layout.length - 1 ? '#cbd5e1' : '#475569'
                    }}
                    title="Move Down"
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>

                {/* Delete / Remove Button */}
                <button
                  type="button"
                  onClick={() => handleRemove(item.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc2626',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Remove from layout"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
